import { PropertyType } from "@/app/[locale]/(public)/rent/[locationSlug]/_components/property-type/utils";
import { locationInfo } from "./info";
import { rentPropertyTypes } from "./property-type";
import { rentLocations } from "./rent-location";
import { resolveLocalizedLocationName } from "./name";

export type LocationOption = {
  label: string;
  value: string;
};

const normalizeLocationName = (value: string) =>
  value.trim().toLocaleLowerCase();

const rentLocationByName = new Map(
  Object.values(rentLocations).flatMap((location) =>
    location.names.map(
      (name) => [normalizeLocationName(name), location] as const,
    ),
  ),
);

export const getRentLocationDisplayName = (
  location: (typeof rentLocations)[string],
  locale: string,
) =>
  resolveLocalizedLocationName(
    {
      name: location.names[0],
      localizedNames: location.localizedNames,
    },
    locale,
  );

export const getLocationDisplayName = (name: string, locale: string) => {
  const location = rentLocationByName.get(normalizeLocationName(name));

  return location ? getRentLocationDisplayName(location, locale) : name;
};

/**
 * Slug lookup by any name a location answers to. Every entry in the generated
 * file has a `/rent/:slug` page regardless of type, so provinces, boroughs and
 * neighbourhoods resolve here just as cities do.
 *
 * Names are not unique — plenty of cities have a "Centrum" — so a name shared
 * by more than one location resolves to null rather than silently linking to
 * whichever slug happened to be first.
 */
const rentLocationSlugByName = (() => {
  const map = new Map<string, string | null>();

  for (const [slug, location] of Object.entries(rentLocations)) {
    for (const name of location.names) {
      const key = normalizeLocationName(name);
      if (!map.has(key)) {
        map.set(key, slug);
        continue;
      }
      if (map.get(key) !== slug) map.set(key, null);
    }
  }

  return map;
})();

export const getRentLocationSlugByName = (name: string) =>
  rentLocationSlugByName.get(normalizeLocationName(name)) ?? null;

/**
 * `/rent/:slug` for a location name, or null when the name is unknown,
 * ambiguous, or does not sit at the expected geographic level.
 */
export const getRentLocationHrefByName = (
  name: string,
  expectedType?: (typeof rentLocations)[string]["type"],
) => {
  const slug = getRentLocationSlugByName(name);
  if (!slug) return null;
  if (expectedType && rentLocations[slug]?.type !== expectedType) return null;
  return `/rent/${slug}`;
};

// Sorted by the name shown in this locale ("The Hague" under T in English),
// with any pinned names first.
const getLocationOptions = (
  names: string[],
  locale: string,
  pinned: Set<string> = new Set(),
): LocationOption[] =>
  names
    .map((name) => ({
      value: name,
      label: getLocationDisplayName(name, locale),
    }))
    .sort(
      (a, b) =>
        Number(pinned.has(b.value)) - Number(pinned.has(a.value)) ||
        a.label.localeCompare(b.label, locale),
    );

export const getTopCitiesSlugNames = (limit: number = 10, locale = "en") => {
  const topCitySlugs = Object.keys(rentLocations)
    .filter((slug) => rentLocations[slug].type === "CITY")
    .sort((a, b) => {
      return (rentLocations[b].count || 0) - (rentLocations[a].count || 0);
    })
    .slice(0, limit);

  const topCities = topCitySlugs.map((slug) => {
    return {
      slug,
      canonicalName: rentLocations[slug].names[0],
      name: getRentLocationDisplayName(rentLocations[slug], locale),
    };
  });

  return topCities;
};

export const topCities = getTopCitiesSlugNames(12).map(
  (city) => city.canonicalName,
);

const sortCitiesWithTopCities = (cities: string[]) => {
  const topCitiesSet = new Set(topCities);
  const sortedCities = cities.sort((a, b) => {
    const isATopCity = topCitiesSet.has(a);
    const isBTopCity = topCitiesSet.has(b);

    if (isATopCity && !isBTopCity) return -1;
    if (!isATopCity && isBTopCity) return 1;

    return a.localeCompare(b);
  });
  return sortedCities;
};

// Every alias of a city or province mapped to its canonical name. Kept per
// type because names like "Utrecht" and "Groningen" are both.
const canonicalNameByAlias = (type: "CITY" | "PROVINCE") =>
  new Map(
    Object.values(rentLocations)
      .filter((location) => location.type === type)
      .flatMap((location) =>
        location.names.map(
          (name) => [normalizeLocationName(name), location.names[0]] as const,
        ),
      ),
  );

const canonicalCityNames = canonicalNameByAlias("CITY");
const canonicalProvinceNames = canonicalNameByAlias("PROVINCE");

const toCanonicalProvince = (name: string) =>
  canonicalProvinceNames.get(normalizeLocationName(name)) ?? name;

// Names without a rent location keep their spelling, minus all-caps.
const toCanonicalCity = (name: string) =>
  canonicalCityNames.get(normalizeLocationName(name)) ??
  (name === name.toUpperCase()
    ? name
        .toLowerCase()
        .replace(
          /(^|[\s-])(\p{L})/gu,
          (_, gap, letter) => gap + letter.toUpperCase(),
        )
    : name);

/**
 * The generated info.ts keeps whichever spelling sorted first, so it lists
 * "AMSTERDAM", "'S-GRAVENHAGE" next to "DEN HAAG", and Friesland twice. Filter
 * options use canonical names, which is what search links put in the URL.
 */
const canonicalLocationInfo = Object.entries(locationInfo).reduce<
  Record<string, string[]>
>((result, [province, cities]) => {
  const key = toCanonicalProvince(province);
  result[key] = Array.from(
    new Set([...(result[key] ?? []), ...cities.map(toCanonicalCity)]),
  ).sort();
  return result;
}, {});

export const getAllLocationCities = () => {
  const allCities: string[] = [];
  Object.values(canonicalLocationInfo).forEach((cities) => {
    allCities.push(...cities);
  });
  const result = Array.from(new Set(allCities)).sort();

  return sortCitiesWithTopCities(result);
};

export const getAllLocationCityOptions = (locale: string) =>
  getLocationOptions(getAllLocationCities(), locale, new Set(topCities));

export const getAllProvinces = () => {
  return Object.keys(canonicalLocationInfo).sort();
};

export const getAllProvinceOptions = (locale: string) =>
  getLocationOptions(getAllProvinces(), locale);

export const getCitiesByProvince = (province: string) => {
  const result = canonicalLocationInfo[toCanonicalProvince(province)] || [];

  return sortCitiesWithTopCities([...result]);
};

export const getCityOptionsByProvince = (province: string, locale: string) =>
  getLocationOptions(getCitiesByProvince(province), locale, new Set(topCities));

export const getRentLocation = (locationSlug: string) => {
  if (!rentLocations[locationSlug]) {
    return null;
  }

  return rentLocations[locationSlug];
};

export const doesLocationHavePropertyType = (
  locationSlug: string,
  propertyType: string,
) => {
  if (
    rentPropertyTypes[locationSlug] &&
    propertyType in rentPropertyTypes[locationSlug]
  ) {
    return true;
  }
  return false;
};

export const getLocationPropertyTypeData = (locationSlug: string) => {
  if (!rentPropertyTypes[locationSlug]) {
    return null;
  }

  return rentPropertyTypes[locationSlug];
};
export const getAllPropertyTypes = (locationSlug: string, skip?: string[]) => {
  if (!rentPropertyTypes[locationSlug]) {
    return [];
  }

  const items = (
    Object.keys(rentPropertyTypes[locationSlug]) as Array<PropertyType>
  )
    .filter((item) => {
      if (skip && skip.includes(item)) {
        return false;
      }
      return true;
    })
    .sort((a, b) => a.localeCompare(b));

  return items;
};
